"use client";
// @ts-strict-ignore
import styled from "styled-components";
import {
  getFontStyles,
  getFontColor,
  getFontWeight,
  getResponsiveProps,
  getTextDecoration,
  getWeight
} from "./utils";
import { robotoSlab } from "public/fonts/robotoSlab";
import * as React from "react";
import type { TextComponentDisplaySize, TextComponentProps } from "./constants";
import { bodyProps } from "./constants";
import { LargeTextResponsive, ReflectableText } from "./styles";
import { Text as HubbleText } from "@hubble/web";
import { Link } from "i18n/routing";
type TypographyScale = {
  fontSize: FontSize;
  letterSpacing: LetterSpacing;
  lineHeight: LineHeight;
};

export type TypographyPresetType = {
  sm?: TextOptions;
  md?: TextOptions;
  lg?: TextOptions;
  mdV1?: TextOptions;
  smV1?: TextOptions;
  xlV1?: TextOptions;
  lgV1?: TextOptions;
};

export type TypographySizePresetType = {
  [key: string]: TypographyPresetType;
};

export enum TextDisplay {
  inline = "inline",
  block = "block",
  flex = "flex",
  none = "none"
}

export enum FontSize {
  px10 = "10px",
  px12 = "12px",
  px14 = "14px",
  px16 = "16px",
  px18 = "18px",
  px20 = "20px",
  px22 = "22px",
  px24 = "24px",
  px25 = "25px",
  px28 = "28px",
  px30 = "30px",
  px32 = "32px",
  px36 = "36px",
  px40 = "40px",
  px44 = "44px",
  px48 = "48px",
  px56 = "56px",
  px60 = "60px",
  px65 = "65px",
  px68 = "68px",
  px80 = "80px",
  px72 = "72px",
  px96 = "96px"
}

export enum LineHeight {
  "em1.1" = "1.1",
  "em1.15" = "1.15",
  "em1.2" = "1.2",
  "em1.3" = "1.3",
  "em1.4" = "1.4",
  "em1.5" = "1.5",
  "em1.6" = "1.6",
  px116 = "116px",
  px92 = "92px",
  px80 = "80px",
  px88 = "88px",
  px65 = "65px",
  px68 = "68px",
  px33 = "33px",
  px25 = "25px",
  px60 = "60px",
  px56 = "56px",
  px48 = "48px",
  px44 = "44px",
  px40 = "40px",
  px36 = "36px",
  px32 = "32px",
  px28 = "28px",
  px26 = "26px",
  px24 = "24px",
  px22 = "22px",
  px20 = "20px",
  px16 = "16px",
  px15 = "15px"
}

export enum LetterSpacing {
  px2 = "2",
  px0 = "0",
  px16 = "-0.16",
  px74 = "-0.74",
  px_3 = "-2.64",
  px_2 = "-2",
  px_25 = "-1.25",
  px64 = "-1",
  "px-0.25" = "-0.25",
  "px-0.5" = "-0.5",
  "px-1" = "-1",
  "px-1.5" = "-1.5",
  "px-2" = "-2",
  "px-2.5" = "-2.5"
}

export enum FontWeight {
  extraBold = "800",
  bold = "700",
  semiBold = "500",
  regular = "400",
  bold600 = "600"
}

export interface TextOptions {
  m?: string;
  ml?: string;
  mr?: string;
  mt?: string;
  mb?: string;
  maxWidth?: string;
  color?: string;
  order?: number;
  display?: TextDisplay | string;
  fontSize?: FontSize | string;
  lineHeight?: LineHeight | string;
  letterSpacing?: LetterSpacing | string;
  fontWeight?: FontWeight | string;
  textAlign?: "center" | "left" | "right" | "justify";
}

export interface TextProps extends React.HTMLAttributes<HTMLParagraphElement> {
  m?: string;
  ml?: string;
  mr?: string;
  mt?: string;
  order?: number;
  mb?: string;
  fontFamily?: string;
  sm?: TextOptions;
  md?: TextOptions;
  lg?: TextOptions;
  xl?: TextOptions;
  as?: string;
  maxWidth?: string;
  forwardedAs?: string;
  bold?: boolean;
  semiBold?: boolean;
  bold600?: boolean;
  color?: string;
  italics?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  children: React.ReactNode;
  textAlign?: "center" | "left" | "right" | "justify";
  textTransform?: "capitalize" | "lowercase" | "uppercase";
  overflowWrap?:
    | "break-word"
    | "normal"
    | "anywhere"
    | "unset"
    | "initial"
    | "inherit";
}

export interface TextSizeProps extends TextProps {
  size?:
    | "xl"
    | "xlV1"
    | "lg"
    | "lgV1"
    | "md"
    | "sm"
    | "mdV1"
    | "smV1"
    | "xs"
    | "xsV1"
    | "xxsV1"
    | "xxs"
    | "smCustom"
    | "xsCustom"
    | "xxsCustom"
    | "xxl"
    | "mdCardSubHeading"
    | "xxlCustom"
    | "mdSubHeading"
    | "lgCustom"
    | "xxsHeading"
    | "xxsSubtitle"
    | "mdCustom";
}

export interface TextSizeV1Props extends TextProps {
  size: "xl" | "lg" | "md" | "sm" | "xs";
}

enum AsDisplay {
  xl = "h1",
  lg = "h1",
  xlV1 = "h1",
  xxl = "h2",
  xxlCustom = "h2",
  mdCardSubHeading = "h2",
  lgCustom = "h3",
  mdCustom = "h1",
  lgV1 = "h2",
  md = "h2",
  mdV1 = "h2",
  sm = "h2",
  smV1 = "h2",
  xxsCustom = "h2",
  smCustom = "h2",
  xsCustom = "h2",
  mdSubHeading = "h2",
  xsV1 = "h2",
  xxsHeading = "h2",
  xxsV1 = "h3",
  xs = "h3"
}

enum AsHeading {
  xl = "h2",
  lg = "h3",
  lgCustom = "h3",
  smCustom = "h2",
  mdCustom = "h2",
  xsCustom = "h2",
  mdSubHeading = "h3",
  md = "h4",
  lgV1 = "h3",
  mdV1 = "h4",
  smV1 = "h5",
  sm = "h5",
  xs = "h6",
  xxsV1 = "h2",
  xxsSubtitle = "h4",
  xxsCustom = "h5",
  xxs = "h6"
}

export const Text = styled.span<TextProps>`
  ${({ m }) => m && `margin: ${m};`}
  ${({ order }) => order && `order: ${order};`}
  ${({ margin }) => margin && `margin: ${margin};`}
  ${({ ml }) => ml && `margin-left: ${ml};`}
  ${({ marginLeft }) => marginLeft && `margin-left: ${marginLeft};`}
  ${({ mr }) => mr && `margin-right: ${mr};`}
  ${({ marginRight }) => marginRight && `margin-right: ${marginRight};`}
  ${({ mt }) => mt && `margin-top: ${mt};`}
  ${({ marginTop }) => marginTop && `margin-top: ${marginTop};`}
  ${({ mb }) => mb && `margin-bottom: ${mb};`}
  ${({ marginBottom }) => marginBottom && `margin-bottom: ${marginBottom};`}
  ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth};`}
  ${({ fontFamily }) => fontFamily && `font-family: ${fontFamily};`}
  ${({ color, theme }) => getFontColor(color, theme)}
  ${({ bold, semiBold, extraBold, bold600 }) =>
    getFontWeight(bold, semiBold, extraBold, bold600)}
  ${({ underline, strikethrough }) =>
    getTextDecoration(underline, strikethrough)}
  ${({ italics }) => !!italics && `font-style: italic;`}
  ${({ textAlign }) => !!textAlign && `text-align: ${textAlign};`}
  ${({ textTransform }) =>
    !!textTransform && `text-transform: ${textTransform};`}
  ${({ sm }) => getFontStyles(sm)}
  ${({ theme, md }) =>
    theme.media.tablet`
      ${getFontStyles(md)}
  `}
  ${({ lg, theme }) =>
    theme.media.desktop`
    ${getFontStyles(lg)}
  `}
  ${({ xl, theme }) =>
    theme.media.largeDesktop`
    ${getFontStyles(xl)}
  `}
    ${({ overflowWrap }) => !!overflowWrap && `overflow-wrap: ${overflowWrap};`}
`;

export const Scales: { [key: string]: TypographyScale } = {
  scale0: {
    fontSize: FontSize.px10,
    letterSpacing: LetterSpacing.px0,
    lineHeight: LineHeight.px15
  },
  scale1: {
    fontSize: FontSize.px12,
    letterSpacing: LetterSpacing.px0,
    lineHeight: LineHeight.px16
  },
  scale2: {
    fontSize: FontSize.px14,
    letterSpacing: LetterSpacing.px0,
    lineHeight: LineHeight.px20
  },
  scale3: {
    fontSize: FontSize.px16,
    letterSpacing: LetterSpacing.px0,
    lineHeight: LineHeight.px24
  },
  scale4: {
    fontSize: FontSize.px18,
    letterSpacing: LetterSpacing.px0,
    lineHeight: LineHeight.px28
  },
  scale5: {
    fontSize: FontSize.px20,
    letterSpacing: LetterSpacing.px0,
    lineHeight: LineHeight.px28
  },
  scale6: {
    fontSize: FontSize.px24,
    letterSpacing: LetterSpacing.px0,
    lineHeight: LineHeight.px32
  },
  scale7: {
    fontSize: FontSize.px28,
    letterSpacing: LetterSpacing.px0,
    lineHeight: LineHeight.px36
  },
  scale8: {
    fontSize: FontSize.px32,
    letterSpacing: LetterSpacing.px0,
    lineHeight: LineHeight.px36
  },
  scale9: {
    fontSize: FontSize.px36,
    letterSpacing: LetterSpacing.px0,
    lineHeight: LineHeight.px44
  },
  scale10: {
    fontSize: FontSize.px40,
    letterSpacing: LetterSpacing.px0,
    lineHeight: LineHeight.px48
  },
  scale11: {
    fontSize: FontSize.px48,
    letterSpacing: LetterSpacing.px0,
    lineHeight: LineHeight.px56
  },
  scale12: {
    fontSize: FontSize.px56,
    letterSpacing: LetterSpacing.px0,
    lineHeight: LineHeight.px68
  },
  scale13: {
    fontSize: FontSize.px72,
    letterSpacing: LetterSpacing.px0,
    lineHeight: LineHeight.px88
  },
  scale14: {
    fontSize: FontSize.px96,
    letterSpacing: LetterSpacing.px0,
    lineHeight: LineHeight.px116
  },
  scale15: {
    fontSize: FontSize.px60,
    letterSpacing: LetterSpacing.px_3,
    lineHeight: LineHeight.px60
  },
  scale16: {
    fontSize: FontSize.px48,
    letterSpacing: LetterSpacing.px_3,
    lineHeight: LineHeight.px48
  },
  scale17: {
    fontSize: FontSize.px44,
    letterSpacing: LetterSpacing.px64,
    lineHeight: LineHeight.px44
  },
  scale18: {
    fontSize: FontSize.px24,
    letterSpacing: LetterSpacing.px64,
    lineHeight: LineHeight.px32
  },
  scale19: {
    fontSize: FontSize.px32,
    letterSpacing: LetterSpacing.px64,
    lineHeight: LineHeight.px36
  },
  scale20: {
    fontSize: FontSize.px30,
    letterSpacing: LetterSpacing.px64,
    lineHeight: LineHeight.px33
  },
  scale21: {
    fontSize: FontSize.px22,
    letterSpacing: LetterSpacing.px64,
    lineHeight: LineHeight.px26
  },
  scale22: {
    fontSize: FontSize.px36,
    letterSpacing: LetterSpacing.px64,
    lineHeight: LineHeight.px40
  },
  scale23: {
    fontSize: FontSize.px96,
    letterSpacing: LetterSpacing.px64,
    lineHeight: LineHeight.px92
  },
  scale24: {
    fontSize: FontSize.px80,
    letterSpacing: LetterSpacing.px64,
    lineHeight: LineHeight.px80
  },
  scale25: {
    fontSize: FontSize.px65,
    letterSpacing: LetterSpacing.px64,
    lineHeight: LineHeight.px65
  },
  scale26: {
    fontSize: FontSize.px25,
    letterSpacing: LetterSpacing.px_25,
    lineHeight: LineHeight.px25
  },
  scale27: {
    fontSize: FontSize.px24,
    letterSpacing: LetterSpacing.px_25,
    lineHeight: LineHeight.px22
  },
  scale28: {
    fontSize: FontSize.px24,
    letterSpacing: LetterSpacing.px74,
    lineHeight: LineHeight.px28
  }
};

export const Typography: { [key: string]: TypographySizePresetType } = {
  Display: {
    xl: {
      sm: Scales.scale10,
      md: Scales.scale11,
      lg: Scales.scale14
    },
    xxl: {
      sm: Scales.scale12,
      md: Scales.scale25,
      lg: Scales.scale23
    },
    xxlCustom: {
      sm: Scales.scale24,
      md: Scales.scale25,
      lg: Scales.scale23
    },
    lg: {
      sm: Scales.scale9,
      md: Scales.scale10,
      lg: Scales.scale13
    },
    lgCustom: {
      sm: Scales.scale10,
      md: Scales.scale10,
      lg: Scales.scale15
    },
    xlV1: {
      sm: Scales.scale16,
      md: Scales.scale16,
      lg: Scales.scale15
    },
    lgV1: {
      sm: Scales.scale16,
      md: Scales.scale16,
      lg: Scales.scale15
    },
    mdV1: {
      sm: Scales.scale17,
      md: Scales.scale17,
      lg: Scales.scale16
    },
    smV1: {
      sm: Scales.scale10,
      md: Scales.scale10,
      lg: Scales.scale10
    },
    mdCustom: {
      sm: Scales.scale8,
      md: Scales.scale8,
      lg: Scales.scale16
    },
    md: {
      sm: Scales.scale6,
      md: Scales.scale8,
      lg: Scales.scale12
    },
    sm: {
      sm: Scales.scale5,
      md: Scales.scale6,
      lg: Scales.scale11
    },
    mdSubHeading: {
      sm: Scales.scale26,
      md: Scales.scale10,
      lg: Scales.scale16
    },
    mdCardSubHeading: {
      sm: Scales.scale6,
      md: Scales.scale11,
      lg: Scales.scale11
    },
    xs: {
      sm: Scales.scale4,
      md: Scales.scale5,
      lg: Scales.scale10
    },
    xsV1: {
      sm: Scales.scale4,
      md: Scales.scale5,
      lg: Scales.scale19
    },
    xxsV1: {
      sm: Scales.scale21,
      md: Scales.scale21,
      lg: Scales.scale21
    },
    smCustom: {
      sm: Scales.scale8,
      md: Scales.scale8,
      lg: Scales.scale8
    },
    xsCustom: {
      sm: Scales.scale7,
      md: Scales.scale7,
      lg: Scales.scale22
    },
    xxsCustom: {
      sm: Scales.scale17,
      md: Scales.scale6,
      lg: Scales.scale6
    },
    xxsHeading: {
      sm: Scales.scale6,
      md: Scales.scale6,
      lg: Scales.scale6
    }
  },
  Heading: {
    xl: {
      sm: Scales.scale6,
      md: Scales.scale8,
      lg: Scales.scale9
    },
    lg: {
      sm: Scales.scale5,
      md: Scales.scale6,
      lg: Scales.scale8
    },
    lgCustom: {
      sm: Scales.scale8,
      md: Scales.scale6,
      lg: Scales.scale9
    },
    lgV1: {
      sm: Scales.scale7,
      md: Scales.scale7,
      lg: Scales.scale9
    },
    mdV1: {
      sm: Scales.scale18,
      md: Scales.scale18,
      lg: Scales.scale19
    },
    smV1: {
      sm: Scales.scale5,
      md: Scales.scale5,
      lg: Scales.scale6
    },
    smCustom: {
      sm: Scales.scale5,
      md: Scales.scale3,
      lg: Scales.scale6
    },
    mdCustom: {
      sm: Scales.scale5,
      md: Scales.scale5,
      lg: Scales.scale7
    },
    md: {
      sm: Scales.scale4,
      md: Scales.scale5,
      lg: Scales.scale6
    },
    sm: {
      sm: Scales.scale3,
      md: Scales.scale4,
      lg: Scales.scale5
    },
    xsCustom: {
      sm: Scales.scale3,
      md: Scales.scale4,
      lg: Scales.scale4
    },
    mdSubHeading: {
      sm: Scales.scale4,
      md: Scales.scale22,
      lg: Scales.scale22
    },
    xs: {
      sm: Scales.scale3,
      md: Scales.scale3,
      lg: Scales.scale3
    },
    xxs: {
      sm: Scales.scale2,
      md: Scales.scale2,
      lg: Scales.scale2
    },
    xxsV1: {
      sm: Scales.scale27,
      md: Scales.scale27,
      lg: Scales.scale26
    },
    xxsCustom: {
      sm: Scales.scale4,
      md: Scales.scale4,
      lg: Scales.scale4
    },
    xxsSubtitle: {
      sm: Scales.scale1,
      md: Scales.scale1,
      lg: Scales.scale1
    }
  },
  Body: {
    xl: {
      sm: Scales.scale5,
      md: Scales.scale5,
      lg: Scales.scale5
    },
    xlV1: {
      sm: Scales.scale4,
      md: Scales.scale4,
      lg: Scales.scale6
    },
    xlCustom: {
      sm: Scales.scale4,
      md: Scales.scale4,
      lg: Scales.scale28
    },
    xxl: {
      sm: Scales.scale3,
      md: Scales.scale3,
      lg: Scales.scale5
    },
    lg: {
      sm: Scales.scale4,
      md: Scales.scale4,
      lg: Scales.scale4
    },
    lgV1: {
      sm: Scales.scale4,
      md: Scales.scale4,
      lg: Scales.scale4
    },
    mdV1: {
      sm: Scales.scale2,
      md: Scales.scale2,
      lg: Scales.scale3
    },
    smV1: {
      sm: Scales.scale2,
      md: Scales.scale2,
      lg: Scales.scale2
    },
    smCustom: {
      sm: Scales.scale1,
      md: Scales.scale1,
      lg: Scales.scale2
    },
    xsV1: {
      sm: Scales.scale3,
      md: Scales.scale3,
      lg: Scales.scale4
    },
    md: {
      sm: Scales.scale3,
      md: Scales.scale3,
      lg: Scales.scale3
    },
    mdCustom: {
      sm: Scales.scale1,
      md: Scales.scale1,
      lg: Scales.scale3
    },
    sm: {
      sm: Scales.scale2,
      md: Scales.scale2,
      lg: Scales.scale2
    },
    xs: {
      sm: Scales.scale1,
      md: Scales.scale1,
      lg: Scales.scale1
    },
    xxs: {
      sm: Scales.scale0,
      md: Scales.scale0,
      lg: Scales.scale0
    }
  }
};

// This is made for the text below h2's that has
// responsive props for boby texts
const SubheadingProps: TypographyPresetType = {
  sm: Scales.scale3,
  md: Scales.scale3,
  lg: Scales.scale4
};

export const Display = React.forwardRef(
  (
    {
      size,
      as = AsDisplay[size],
      bold = true,
      m = "0 0 16px",
      sm,
      md,
      lg,
      ...props
    }: TextSizeProps,
    ref
  ) => (
    <Text
      bold={bold}
      as={as}
      ref={ref}
      m={m}
      {...{
        ...getResponsiveProps(Typography.Display[size], { sm, md, lg }),
        ...props
      }}
    />
  )
);
export const DisplayV1 = React.forwardRef(
  (
    {
      size,
      as = AsDisplay[size],
      bold600 = true,
      m = "0 0 16px",
      sm,
      md,
      lg,
      ...props
    }: TextSizeProps,
    ref
  ) => (
    <Text
      bold600={bold600}
      as={as}
      ref={ref}
      m={m}
      overflowWrap="break-word"
      {...{
        ...getResponsiveProps(Typography.Display[size], { sm, md, lg }),
        ...props
      }}
    />
  )
);
export const DSDisplay = React.forwardRef(
  (
    {
      size,
      as = AsDisplay[size],
      bold = true,
      m = "0",
      sm,
      md,
      lg,
      ...props
    }: TextSizeProps,
    ref
  ) => (
    <Text
      bold={bold}
      as={as}
      ref={ref}
      m={m}
      fontFamily={robotoSlab.style.fontFamily}
      {...{
        ...getResponsiveProps(Typography.Display[size], { sm, md, lg }),
        ...props
      }}
    />
  )
);

export const Heading = React.forwardRef(
  (
    {
      size,
      as = AsHeading[size],
      semiBold = true,
      m = "0 0 16px",
      sm,
      md,
      lg,
      ...props
    }: TextSizeProps,
    ref
  ) => (
    <Text
      semiBold={semiBold}
      as={as}
      ref={ref}
      m={m}
      {...{
        ...getResponsiveProps(Typography.Heading[size], { sm, md, lg }),
        ...props
      }}
    />
  )
);
export const HeadingV1 = React.forwardRef(
  (
    {
      size,
      as = AsHeading[size],
      bold600 = true,
      m = "0 0 16px",
      sm,
      md,
      lg,
      ...props
    }: TextSizeProps,
    ref
  ) => (
    <Text
      bold600={bold600}
      as={as}
      ref={ref}
      m={m}
      {...{
        ...getResponsiveProps(Typography.Heading[size], { sm, md, lg }),
        ...props
      }}
    />
  )
);
// uses roboto slab
export const DSHeading = React.forwardRef(
  (
    {
      size,
      as = AsHeading[size],
      semiBold = true,
      m = "0",
      sm,
      md,
      lg,
      ...props
    }: TextSizeProps,
    ref
  ) => (
    <Text
      semiBold={semiBold}
      as={as}
      ref={ref}
      m={m}
      fontFamily={robotoSlab.style.fontFamily}
      {...{
        ...getResponsiveProps(Typography.Heading[size], { sm, md, lg }),
        ...props
      }}
    />
  )
);

export const Body = React.forwardRef(
  (
    { size, as = "p", m = "0 0 8px", sm, md, lg, ...props }: TextSizeProps,
    ref
  ) => (
    <Text
      as={as}
      ref={ref}
      m={m}
      {...{
        ...getResponsiveProps(Typography.Body[size], { sm, md, lg }),
        ...props
      }}
    />
  )
);

// uses roboto slab
export const DSBody = React.forwardRef(
  ({ size, as = "div", m = "0", sm, md, lg, ...props }: TextSizeProps, ref) => (
    <Text
      as={as}
      ref={ref}
      m={m}
      {...{
        ...getResponsiveProps(Typography.Body[size], { sm, md, lg }),
        ...props
      }}
    />
  )
);

export const ResponsiveBody = React.forwardRef(
  ({ as = "p", m = "0", sm, md, lg, ...props }: TextProps, ref) => (
    <Text
      as={as}
      ref={ref}
      m={m}
      {...{ ...getResponsiveProps(SubheadingProps, { sm, md, lg }), ...props }}
    />
  )
);

export const HubbleDisplay = React.forwardRef(
  (
    {
      letterSpacing = "-0.04em",
      size = "md",
      bold,
      semiBold,
      ...rest
    }: TextComponentProps<TextComponentDisplaySize>,
    ref
  ) => {
    return (
      <LargeTextResponsive
        textType="display"
        textSize={size}
        fontWeight={getWeight(bold, semiBold)}
        letterSpacing={letterSpacing}
        {...rest}
      />
    );
  }
);

export const HubbleHeading = React.forwardRef(
  (
    {
      letterSpacing = "-0.03em",
      size = "md",
      bold,
      semiBold,
      ...rest
    }: TextComponentProps,
    ref
  ) => {
    return (
      <LargeTextResponsive
        textType="heading"
        textSize={size}
        fontWeight={getWeight(bold, semiBold)}
        letterSpacing={letterSpacing}
        {...rest}
      />
    );
  }
);

export const HubbleBody = React.forwardRef(
  (
    {
      letterSpacing = "-0.01em",
      size = "md",
      bold,
      semiBold,
      ...rest
    }: TextComponentProps,
    ref
  ) => {
    return (
      <ReflectableText
        {...bodyProps[size]}
        fontWeight={getWeight(bold, semiBold)}
        letterSpacing={letterSpacing}
        {...rest}
      />
    );
  }
);

export const HubbleLink = React.forwardRef(
  (
    {
      letterSpacing = "-0.01em",
      size = "md",
      bold,
      semiBold,
      ...rest
    }: Omit<TextComponentProps, "reflectableContent"> &
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
    ref
  ) => {
    return (
      <HubbleText.Link
        {...bodyProps[size]}
        fontWeight={getWeight(bold, semiBold)}
        letterSpacing={letterSpacing}
        as={Link}
        {...rest}
      />
    );
  }
);
